/**
 * 通知模板编辑 Drawer
 * 根据 notification/frontend-spec.md 规范定义
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Drawer, Form, Input, Select, Switch, Button, Space, message } from 'antd';
import { updateNotificationTemplate } from '@/services/notification/notificationService';
import type { NotificationTemplateDTO } from '@/types/notification/notificationTemplate';

interface Props {
  visible: boolean;
  record: NotificationTemplateDTO | null;
  onClose: () => void;
  onSuccess: () => void;
}

const NotificationTemplateForm: React.FC<Props> = ({
  visible,
  record,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  // 加载数据
  useEffect(() => {
    if (visible && record) {
      form.setFieldsValue({
        templateCode: record.templateCode,
        templateName: record.templateName,
        notificationType: record.notificationType,
        channel: record.channel,
        titleTemplate: record.titleTemplate,
        contentTemplate: record.contentTemplate,
        variables: record.variables,
        isEnabled: record.isEnabled,
      });
    }
  }, [visible, record, form]);

  // 提交
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const response = await updateNotificationTemplate({
        templateCode: record?.templateCode || '',
        templateName: values.templateName,
        titleTemplate: values.titleTemplate,
        contentTemplate: values.contentTemplate,
        isEnabled: values.isEnabled,
      });

      if (response.success) {
        message.success(t('notification.template.list.message.updateSuccess'));
        form.resetFields();
        onSuccess();
      } else {
        message.error(response.errorMessage || t('common.message.operationFailed'));
      }
    } catch (error) {
      console.error('Failed to update template:', error);
    } finally {
      setLoading(false);
    }
  };

  // 取消
  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  return (
    <Drawer
      title={t('notification.template.form.title.edit')}
      placement="right"
      width={720}
      open={visible}
      onClose={handleCancel}
      destroyOnClose
      footer={
        <div style={{ textAlign: 'right' }}>
          <Space>
            <Button onClick={handleCancel}>
              {t('common.cancel')}
            </Button>
            <Button type="primary" loading={loading} onClick={handleSubmit}>
              {t('common.save')}
            </Button>
          </Space>
        </div>
      }
    >
      <Form
        form={form}
        layout="vertical"
        requiredMark="optional"
      >
        <Form.Item
          name="templateCode"
          label={t('notification.template.form.fields.templateCode')}
        >
          <Input disabled />
        </Form.Item>

        <Form.Item
          name="templateName"
          label={t('notification.template.form.fields.templateName')}
          rules={[
            { required: true, message: t('notification.template.form.rules.templateNameRequired') },
            { max: 100, message: 'Max 100 characters' },
          ]}
        >
          <Input placeholder={t('notification.template.form.placeholder.templateName')} />
        </Form.Item>

        <Form.Item
          name="notificationType"
          label={t('notification.template.form.fields.notificationType')}
        >
          <Input disabled />
        </Form.Item>

        <Form.Item
          name="channel"
          label={t('notification.template.form.fields.channel')}
        >
          <Input disabled />
        </Form.Item>

        <Form.Item
          name="titleTemplate"
          label={t('notification.template.form.fields.titleTemplate')}
          rules={[
            { required: true, message: t('notification.template.form.rules.titleTemplateRequired') },
            { max: 200, message: 'Max 200 characters' },
          ]}
        >
          <Input placeholder={t('notification.template.form.placeholder.titleTemplate')} />
        </Form.Item>

        <Form.Item
          name="contentTemplate"
          label={t('notification.template.form.fields.contentTemplate')}
          rules={[
            { required: true, message: t('notification.template.form.rules.contentTemplateRequired') },
            { max: 500, message: 'Max 500 characters' },
          ]}
        >
          <Input.TextArea
            rows={4}
            placeholder={t('notification.template.form.placeholder.contentTemplate')}
          />
        </Form.Item>

        <Form.Item
          name="variables"
          label={t('notification.template.form.fields.variables')}
          tooltip="Use ${variableName} format in title and content templates"
        >
          <Select
            mode="tags"
            placeholder={t('notification.template.form.placeholder.variables')}
            style={{ width: '100%' }}
          />
        </Form.Item>

        <Form.Item
          name="isEnabled"
          label={t('notification.template.form.fields.isEnabled')}
          valuePropName="checked"
        >
          <Switch />
        </Form.Item>
      </Form>
    </Drawer>
  );
};

export default NotificationTemplateForm;
